// Branding extension point: the per-customer identity that is baked into the desktop
// build at packaging time (product name, app id, icons, deeplink scheme), plus the
// auto-update switch and the Windows installer target.
//
// STRICT ON PURPOSE. Unlike the /api/me wire contracts (which use `.catchall` so an
// additive server change does not break older clients), every extension-point schema
// rejects unknown keys: a typo in a customer repo must fail `validate` loudly instead
// of being silently dropped.
//
// Only JSON-Schema-representable types are used here (no `z.date()`, no transforms),
// because the published JSON Schema is generated from these schemas with
// `z.toJSONSchema()`.
import { z } from 'zod';
/** Icon set, as paths relative to the customer repo root. */
export const BrandingIconsSchema = z.strictObject({
    /** macOS bundle icon. */
    icns: z.string().min(1),
    /** Windows installer + exe icon. */
    ico: z.string().min(1),
    /** Linux and in-app icon. */
    png: z.string().min(1),
});
/** Accent colours used by the renderer theme. CSS colour strings. */
export const BrandingColorsSchema = z.strictObject({
    primary: z.string().min(1),
    accent: z.string().min(1).optional(),
});
/**
 * Customer-facing copy overrides, per locale. The product ships `en` + `hu`; a key
 * absent here keeps the product default — including a whole absent locale, so overriding
 * only the Hungarian copy needs no empty `en: {}` counterpart.
 */
export const BrandingStringsSchema = z.strictObject({
    en: z.record(z.string(), z.string()).optional(),
    hu: z.record(z.string(), z.string()).optional(),
});
/**
 * Auto-update feed. `enabled: false` short-circuits the updater entirely — that is the
 * operations-managed rollout path (MSI + central deployment tooling), where the bank's
 * operators push new versions instead of the client pulling them.
 */
export const AutoUpdateSchema = z.strictObject({
    enabled: z.boolean(),
    /**
     * electron-updater `generic` feed URL. Required in practice when `enabled` is true.
     * http/https only — a bare `host:port` string would otherwise pass as a valid URL.
     */
    feedUrl: z.url({ protocol: /^https?$/ }).optional(),
});
/** Windows installer target. `nsis` is per-user with auto-update; `msi` is per-machine. */
export const WinTargetSchema = z.enum(['nsis', 'msi']);
/**
 * MSI-specific options. `upgradeCode` MUST stay stable across product versions —
 * changing it breaks the silent upgrade path for every already-installed machine.
 */
export const MsiOptionsSchema = z.strictObject({
    perMachine: z.boolean(),
    upgradeCode: z.uuid(),
});
/**
 * Who signs the installer: we (vendor) or the customer's own signing service. `certRef`
 * names a secret/certificate reference — never the certificate or its password.
 */
export const CodeSigningSchema = z.strictObject({
    provider: z.enum(['vendor', 'customer']),
    certRef: z.string().min(1).optional(),
});
export const BrandingSchema = z.strictObject({
    /** Product name shown in the installer, the window title and the OS app list. */
    appName: z.string().min(1),
    /** Reverse-DNS application identifier (electron-builder `appId`). */
    appId: z.string().min(1),
    /**
     * Deeplink scheme the installed app registers for and accepts (`<scheme>://…`). Defaults to
     * the product's own `echowork` (EW-143); a distribution that runs next to another EchoWork
     * install on one machine MUST set its own (recommended shape `<customer>-<product>`), or the
     * OS hands its links to whichever install it likes. The legacy `openwork` is retired: the
     * fork produces no such link (the auth callback is loopback), so nothing depends on it.
     */
    protocolScheme: z.string().min(1).default('echowork'),
    icons: BrandingIconsSchema,
    colors: BrandingColorsSchema.optional(),
    strings: BrandingStringsSchema.optional(),
    autoUpdate: AutoUpdateSchema,
    winTarget: WinTargetSchema.default('nsis'),
    msi: MsiOptionsSchema.optional(),
    codeSigning: CodeSigningSchema,
});
//# sourceMappingURL=branding.js.map